// ProofSummary — ADMIN-ONLY display of how a payment was proven. One component for the Payments ledger,
// the student detail row and the dashboard row; students never receive these fields.
// Shows nothing for a payment with no proof. Fields a given proof type never has (a cash payment's
// reference, a backfilled payment's payer) are simply left out.
import { useProofTypes } from "../../hooks/useProofTypes.js";
import { formatKes } from "../../lib/formatMoney.js";
import { PROOF_TYPE_CASH } from "@dm-lms/shared/constants";
import type { PaymentProof } from "@dm-lms/shared/types";

interface ProofSummaryProps {
  payment: Pick<PaymentProof, "proofType" | "proofReference" | "proofPayer" | "proofReceiver" | "proofAmountKes">;
}

export default function ProofSummary({ payment }: ProofSummaryProps) {
  return payment.proofType === null ? null : <ProofDetails payment={payment} proofType={payment.proofType} />;
}

function ProofDetails({ payment, proofType }: ProofSummaryProps & { proofType: string }) {
  const { options } = useProofTypes();
  const label = options.find((option) => option.key === proofType)?.label ?? proofType;
  const isCash = proofType === PROOF_TYPE_CASH;

  return (
    <div className="mt-1 text-xs text-dm-slate" data-testid="proof-summary">
      <span className="font-medium text-dm-navy">{label}</span>
      {payment.proofReference && <span className="ml-1 font-mono">{payment.proofReference}</span>}
      {isCash && payment.proofPayer && payment.proofReceiver && (
        <span className="ml-1">
          · {payment.proofPayer} → {payment.proofReceiver}
        </span>
      )}
      {payment.proofAmountKes !== null && <span className="ml-1">· {formatKes(payment.proofAmountKes)}</span>}
    </div>
  );
}
